'use client';

// react
import { useCallback, useEffect, useState } from 'react';
// @mui
import {
  Card,
  CardHeader,
  Avatar,
  CardContent,
  Typography,
  Box,
  Divider,
  AvatarGroup,
  Grid,
  Button,
  Container,
  Stack,
  CardMedia,
  CardActions,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  TextField,
  Rating,
  Link as MuiLink,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ContentCopy,
  Favorite as FavoriteIcon,
  DateRange as DateRangeIcon,
  Share as ShareIcon,
  Comment as CommentIcon,
  KeyboardDoubleArrowUp,
} from '@mui/icons-material';
// next
import { useRouter } from 'next/navigation';
// utils
import imageLoader from '@/utils/imageLoader';
// enums
import {
  EVENT_PROGRESS,
  EVENT_TYPE,
  PITCH_EVENT_TYPE,
  EVENT_REGISTERED,
  PAYMENT_TYPE,
  USER_GROUP_TYPE,
  STATUS,
} from '@/enums';
// services
import { ContentsService, DashboardUserGroupsContentsService } from '@/services';
// react-toastify
import { toast } from 'react-toastify';
// hooks
import { useAuth, useModal, useSelectedUserGroup } from '@/hooks';
// images
import { noImage } from '@/images';
// constants
import { paths } from '@/routes/paths';
// containers
import { openShareButton } from '@/containers/ShareOptionsContainer';
//
import ContentOptions from './ContentOptions';
import ContentRemainingTime from './ContentRemainingTime';
import InviteOnlyPitchEventRegisterModal from './modals/InviteOnlyPitchEventRegisterModal';

//--------------------------------------------------------------------------

const getContentDetail = (content) => {
  switch (content?.type) {
    case EVENT_TYPE.MEETING:
      return content?.meeting;
    case EVENT_TYPE.TRAINING_SESSION:
      return content?.trainingSession;
    case EVENT_TYPE.PITCH_EVENT:
      return content?.pitchEvent;
    default:
      return {};
  }
};

//--------------------------------------------------------------------------

export default function ContentDetails({ content, mutate, toggleComments }) {
  // -----------------------------------------------

  const currentUserGroup = useSelectedUserGroup();

  const { push } = useRouter();

  const { user } = useAuth();

  const {
    login: { setOpen: setLoginModalOpen },
    events: { setOpen: setEventsModalOpen, setProps: setEventsModalProps },
    payment: { setOpen: setPaymentModalOpen, setProps: setPaymentModalProps },
    selectStartup: { setOpen: setSelectStartupOpen, setProps: setSelectStartupProps },
    createStartup: { setOpen: setCreateStartupModalOpen },
  } = useModal();

  const [modalOpen, setModalOpen] = useState(false);
  const [actionButtonLoading, setActionButtonLoading] = useState(false);
  const [manualZoomLink, setManualZoomLink] = useState('');
  const [copiedAlertOpen, setCopiedAlertOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [contentDescription, setContentDescription] = useState(null);

  const {
    id,
    userGroupId,
    photo,
    title,
    price,
    description,
    likes,
    likedCount,
    commentCount,
    type,
    isLike,
    isPaid,
    userGroup,
    owner,
    registered,
    externalLink,
    userId,
    isEventStartActive,
    isEventJoinActive,
    isJoinByCodeActive,
    eventProgress,
    isInvited,
    meetingId,
    meetingPassword,
    user: {
      detail: { firstName, lastName, profilePhoto },
    },
    parentId,
    affiliateCommissionPercentage,
    ratingScore,
    totalRatingCount,
  } = content ?? {};
  const { title: userGroupTitle, userGroupType } = userGroup ?? {};

  const details = getContentDetail(content);
  const endDate = details?.endDate ? new Date(details?.endDate) : null;

  const meetingIdStr = meetingId?.toString();
  //------------------------------------------------------------------

  useEffect(() => {
    setEventsModalProps({ userGroupId, mutate, eventType: undefined });
  }, [mutate, setEventsModalProps, userGroupId]);

  useEffect(() => {
    if (!contentDescription) {
      import('html-react-parser').then((res) => {
        setContentDescription(res.default(description ?? ' '));
      });
    }
  }, [contentDescription, description]);

  useEffect(() => {
    async function initStartDate() {
      const moment = (await import('moment-timezone')).default;

      setStartDate(
        moment(details?.startDate)
          ?.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
          .format('ddd, MMM D, hh:mm A (zz)')
      );
    }
    if (details?.startDate) initStartDate();
  }, [details?.startDate]);

  //------------------------------------------------------------------

  const handleStart = async () => {
    setActionButtonLoading(true);
    const { success, ...response } = await ContentsService.getMeetingsStart(id);
    if (success) {
      window.open(response.data.startUrl, '_blank');
    }
    setActionButtonLoading(false);
  };

  const handleJoin = async () => {
    setActionButtonLoading(true);
    const { success, ...response } = await ContentsService.getMeetingsJoin(id);
    if (success) {
      window.open(response.data.joinUrl, '_blank');
      setManualZoomLink(response.data.joinUrl);
    }
    setActionButtonLoading(false);
  };

  const handleRegister = async () => {
    setActionButtonLoading(true);
    const { success, data = {} } = await ContentsService.postBuy(id);
    if (success) {
      if (data.clientSecret) {
        setPaymentModalProps({
          contentId: id,
          userGroupPlanId: null,
          secret: data.clientSecret,
          paymentType: PAYMENT_TYPE.CONTENT,
          onSuccess: async () => {
            setActionButtonLoading(true);
            await mutate();
            setActionButtonLoading(false);
          },
          onClose: async () => {
            setActionButtonLoading(true);
            await mutate();
            setActionButtonLoading(false);
          },
        });
        setPaymentModalOpen(true);
      } else {
        await mutate();
      }
    }
    setActionButtonLoading(false);
  };

  const handleJoinByCode = useCallback(async () => {
    setActionButtonLoading(true);
    const { success, ...response } = await ContentsService.getMeetingsJoin(id, joinCode);
    if (success) {
      window.open(response.data.joinUrl, '_blank');
      setManualZoomLink(response.data.joinUrl);
    }
    setActionButtonLoading(false);
  }, [id, joinCode]);

  const handleRegisterByCode = useCallback(async () => {
    setActionButtonLoading(true);
    const { success } = await ContentsService.postBuy(id, joinCode);
    if (success) {
      toast.success('Registered successfully!');
      await mutate();
    }
    setActionButtonLoading(false);
  }, [id, joinCode, mutate]);

  const handleLikeContent = async () => {
    await ContentsService.putLike(id);
    mutate();
  };

  const handleShareAffiliateLink = useCallback(
    (e) => {
      const url = new URL(window.location.href);
      if (url.pathname.endsWith('/')) {
        url.pathname = url.pathname.slice(0, -1);
      }
      url.searchParams.set('referrer', user?.id);
      url.searchParams.set('cpayer', userId);
      url.searchParams.set('t', Date.now());

      openShareButton(e, url);
    },
    [user?.id, userId]
  );

  const handleShare = (e) => {
    const url = new URL(window.location.href);
    if (url.pathname.endsWith('/')) {
      url.pathname = url.pathname.slice(0, -1);
    }

    if (user?.id) url.searchParams.set('referrer', user?.id);

    openShareButton(e, url);
  };

  const handleLogin = () => {
    setLoginModalOpen(true);
  };

  const handleApplyToPitch = () => {
    if (
      user?.userGroupMembers?.some(
        ({ userGroup }) => userGroup.userGroupType === USER_GROUP_TYPE.STARTUP
      )
    ) {
      if (
        user?.userGroupMembers?.some(
          ({ userGroup }) =>
            userGroup.userGroupType === USER_GROUP_TYPE.STARTUP &&
            userGroup?.status === STATUS.ACTIVE
        )
      ) {
        setSelectStartupProps({
          startups:
            user?.userGroupMembers
              ?.filter((item) => item.userGroup.userGroupType === USER_GROUP_TYPE.STARTUP)
              ?.map(({ userGroup }) => userGroup) ?? [],
          onSelect: () => {
            setModalOpen(true);
          },
        });
        setSelectStartupOpen(true);
      } else {
        toast.warning(
          "You don't have any active page. Visit page management to check your startup pages"
        );
      }
    } else setCreateStartupModalOpen(true);
  };

  const onDelete = async () => {
    const { success, ...res } = await DashboardUserGroupsContentsService.deleteContents(id);
    if (success) {
      toast.success('Content Deleted!');
      push(paths.events);
    } else {
      toast.error(res.errorMessage);
    }
  };
  const onEdit = async () => {
    setEventsModalProps({
      eventId: id,
      userGroupId,
      eventType: undefined,
    });
    setEventsModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(manualZoomLink);
    setCopiedAlertOpen(true);
  };

  const handleCopiedAlertClose = () => {
    setCopiedAlertOpen(false);
  };

  const handleShowRatings = useCallback(() => {
    const sectionElement = document.getElementById('ratings');
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <Snackbar open={copiedAlertOpen} autoHideDuration={2000} onClose={handleCopiedAlertClose}>
        <Alert onClose={handleCopiedAlertClose} severity="success" sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
      <InviteOnlyPitchEventRegisterModal
        open={modalOpen}
        setOpen={setModalOpen}
        userGroupId={userGroupId}
        contentId={id}
        userGroupType={userGroupType}
        mutate={mutate}
      />
      <Box sx={{ height: '100%', width: '100%' }}>
        <Box justifyContent="center">
          <Card
            sx={{
              width: '100%',
              minHeight: '100%',

              backgroundColor: '#ffffff',
              boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.1)',
              borderRadius: '5px',
            }}
          >
            <CardHeader
              className="eventPostHeader"
              sx={{ mb: 2 }}
              avatar={
                <a target="_blank" href={`${paths.users}/${userId}`} rel="noreferrer">
                  <Avatar src={imageLoader({ src: profilePhoto ? profilePhoto : '' })} />
                </a>
              }
              title={
                <a target="_blank" href={`${paths.users}/${userId}`} rel="noreferrer">
                  {`${firstName} ${lastName}`}
                </a>
              }
              action={
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Typography className="eventSearchFee">
                    {isPaid ? `$${price}` : 'FREE'}
                  </Typography>
                  {owner && <ContentOptions onEdit={onEdit} onDelete={onDelete} />}
                </Stack>
              }
            />
            <CardMedia
              component="img"
              image={imageLoader({ src: photo ? photo : '' })}
              onError={(e) => (e.target.src = noImage.src)}
              alt=""
              sx={{ mt: 0 }}
            />
            {[EVENT_TYPE.PITCH_EVENT, EVENT_TYPE.TRAINING_SESSION].includes(type) && (
              <Typography
                sx={{
                  zIndex: 1,
                  position: 'absolute',
                  padding: '10px',
                  top: '80px',
                  left: 0,
                  backgroundColor: ' #23aacd',
                  color: '#ffffff',
                  borderRadius: '0px 0px 5px 0px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '20px',
                }}
              >
                {type === EVENT_TYPE.PITCH_EVENT && 'Pitch'}
                {type === EVENT_TYPE.TRAINING_SESSION && 'Training'}
              </Typography>
            )}
            <CardContent>
              <Stack flexDirection="column" spacing={2} sx={{ mt: 3 }}>
                <Typography className="cardTitle">{title}</Typography>
                {Boolean(ratingScore) && (
                  <MuiLink color="black" onClick={handleShowRatings} component="button" ml={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Rating readOnly value={ratingScore} precision={0.1} size="small" />
                      <Typography variant="body1">
                        {ratingScore} stars ({totalRatingCount} review
                        {totalRatingCount > 1 ? 's' : ''})
                      </Typography>
                    </Stack>
                  </MuiLink>
                )}
                <Grid container justifyContent={'space-between'}>
                  <Grid item xs={12} md={6}>
                    <Typography className="eventPostTitle1">{userGroupTitle}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack
                      spacing={1}
                      textAlign="center"
                      direction="column"
                      justifyContent="center"
                      alignItems="stretch"
                    >
                      {owner && (
                        <LoadingButton
                          variant="contained"
                          loading={actionButtonLoading}
                          sx={{ color: 'white' }}
                          onClick={handleStart}
                          disabled={!isEventStartActive}
                        >
                          Start
                        </LoadingButton>
                      )}
                      {!owner && (
                        <>
                          {/* Past Content */}
                          {eventProgress === EVENT_PROGRESS.PAST && (
                            <Typography
                              color={(theme) => theme.palette.grey[500]}
                              fontWeight="bold"
                            >
                              Past Event
                            </Typography>
                          )}
                          {/* Happening Now */}
                          {(eventProgress === EVENT_PROGRESS.NOW ||
                            (isEventJoinActive && eventProgress !== EVENT_PROGRESS.PAST)) && (
                            <>
                              {registered !== EVENT_REGISTERED.ACTIVE &&
                                (!isInvited &&
                                type === EVENT_TYPE.PITCH_EVENT &&
                                details.pitchEventType === PITCH_EVENT_TYPE.INVITE_ONLY ? (
                                  userGroupType === USER_GROUP_TYPE.INVESTOR ? (
                                    registered !== EVENT_REGISTERED.PENDING &&
                                    currentUserGroup?.userGroupType === USER_GROUP_TYPE.STARTUP ? (
                                      <>
                                        <Button
                                          variant="contained"
                                          sx={{ color: 'white' }}
                                          onClick={user ? handleApplyToPitch : handleLogin}
                                        >
                                          Apply to Pitch
                                        </Button>
                                        <Typography
                                          color={(theme) => theme.palette.error.main}
                                          fontWeight="bold"
                                          variant="subtitle2"
                                        >
                                          This event is invite-only.
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        {isEventJoinActive && isJoinByCodeActive && (
                                          <>
                                            <TextField
                                              label="Join Code"
                                              value={joinCode}
                                              onChange={(event) =>
                                                setJoinCode(event?.target?.value)
                                              }
                                            />
                                            <LoadingButton
                                              loading={actionButtonLoading}
                                              variant="contained"
                                              sx={{ color: 'white' }}
                                              onClick={user ? handleJoinByCode : handleLogin}
                                            >
                                              Join by Code
                                            </LoadingButton>
                                          </>
                                        )}
                                        <Typography
                                          color={(theme) => theme.palette.error.main}
                                          fontWeight="bold"
                                          variant="subtitle2"
                                        >
                                          You must be invited to join this event.
                                        </Typography>
                                      </>
                                    )
                                  ) : registered !== EVENT_REGISTERED.PENDING ? (
                                    <>
                                      {isEventJoinActive && isJoinByCodeActive && (
                                        <>
                                          <TextField
                                            label="Join Code"
                                            value={joinCode}
                                            onChange={(event) => setJoinCode(event?.target?.value)}
                                          />
                                          <LoadingButton
                                            loading={actionButtonLoading}
                                            variant="contained"
                                            sx={{ color: 'white' }}
                                            onClick={user ? handleJoinByCode : handleLogin}
                                          >
                                            Join by Code
                                          </LoadingButton>
                                        </>
                                      )}
                                      <Typography
                                        color={(theme) => theme.palette.error.main}
                                        fontWeight="bold"
                                        variant="subtitle2"
                                      >
                                        You must be invited to join this event.
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography color="primary.main" fontWeight="bold">
                                      Pending
                                    </Typography>
                                  )
                                ) : (
                                  !parentId && (
                                    <>
                                      <LoadingButton
                                        variant="contained"
                                        sx={{ color: 'white' }}
                                        loading={actionButtonLoading}
                                        onClick={user ? handleRegister : handleLogin}
                                      >
                                        {!isPaid && 'Register'}
                                        {isPaid && `Register $${price}`}
                                      </LoadingButton>
                                      {type === EVENT_TYPE.PITCH_EVENT &&
                                        details.pitchEventType === PITCH_EVENT_TYPE.INVITE_ONLY && (
                                          <>
                                            {isEventJoinActive && isJoinByCodeActive && (
                                              <>
                                                <Divider>OR</Divider>
                                                <TextField
                                                  label="Join Code"
                                                  value={joinCode}
                                                  onChange={(event) =>
                                                    setJoinCode(event?.target?.value)
                                                  }
                                                />
                                                <LoadingButton
                                                  loading={actionButtonLoading}
                                                  variant="contained"
                                                  sx={{ color: 'white' }}
                                                  onClick={user ? handleJoinByCode : handleLogin}
                                                >
                                                  Join by Code
                                                </LoadingButton>
                                              </>
                                            )}
                                            <Typography
                                              color={(theme) => theme.palette.error.main}
                                              fontWeight="bold"
                                              variant="subtitle2"
                                            >
                                              You must be invited to join this event.
                                            </Typography>
                                          </>
                                        )}
                                    </>
                                  )
                                ))}
                              {registered === EVENT_REGISTERED.ACTIVE &&
                                (externalLink ? (
                                  <Button
                                    target="_blank"
                                    href={externalLink ?? ''}
                                    variant="contained"
                                  >
                                    Click to go to event
                                  </Button>
                                ) : (
                                  <>
                                    {Boolean(meetingId && meetingPassword) && (
                                      <>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                          Zoom Meeting ID:
                                        </Typography>
                                        <Typography variant="body1">
                                          {meetingIdStr.substring(0, 3)}{' '}
                                          {meetingIdStr.substring(3, 7)}{' '}
                                          {meetingIdStr.substring(7, 11)}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                          Zoom Meeting Password:
                                        </Typography>
                                        <Typography variant="body1">{meetingPassword}</Typography>
                                        {manualZoomLink && (
                                          <>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                              Zoom Meeting Link:
                                            </Typography>
                                            <TextField
                                              value={manualZoomLink}
                                              variant="outlined"
                                              readOnly
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <Tooltip title="Copy link to clipboard">
                                                      <a>
                                                        <IconButton onClick={handleCopyLink}>
                                                          <ContentCopy />
                                                        </IconButton>
                                                      </a>
                                                    </Tooltip>
                                                  </InputAdornment>
                                                ),
                                              }}
                                            />
                                          </>
                                        )}
                                      </>
                                    )}
                                    {!(meetingId && meetingPassword) &&
                                      registered === EVENT_REGISTERED.ACTIVE && (
                                        <Typography color="error">
                                          The host has not started the event yet.
                                        </Typography>
                                      )}
                                    <LoadingButton
                                      variant="contained"
                                      loading={
                                        !(meetingId && meetingPassword) && actionButtonLoading
                                      }
                                      onClick={registered === EVENT_REGISTERED.ACTIVE && handleJoin}
                                      disabled={!(meetingId && meetingPassword)}
                                    >
                                      {registered === EVENT_REGISTERED.ACTIVE &&
                                        ' Click to Join Zoom Meeting'}
                                    </LoadingButton>
                                  </>
                                ))}
                            </>
                          )}
                          {/* Upcoming */}
                          {eventProgress === EVENT_PROGRESS.UPCOMING && !isEventJoinActive && (
                            <>
                              {registered !== EVENT_REGISTERED.ACTIVE &&
                                (!isInvited &&
                                type === EVENT_TYPE.PITCH_EVENT &&
                                details.pitchEventType === PITCH_EVENT_TYPE.INVITE_ONLY ? (
                                  userGroupType === USER_GROUP_TYPE.INVESTOR ? (
                                    registered !== EVENT_REGISTERED.PENDING &&
                                    currentUserGroup?.userGroupType === USER_GROUP_TYPE.STARTUP ? (
                                      <>
                                        <Button
                                          variant="contained"
                                          sx={{ color: 'white' }}
                                          onClick={user ? handleApplyToPitch : handleLogin}
                                        >
                                          Apply to Pitch
                                        </Button>
                                        <Typography
                                          color={(theme) => theme.palette.error.main}
                                          fontWeight="bold"
                                          variant="subtitle2"
                                        >
                                          This event is invite-only.
                                        </Typography>
                                      </>
                                    ) : (
                                      <>
                                        {isJoinByCodeActive && (
                                          <>
                                            <TextField
                                              label="Join Code"
                                              value={joinCode}
                                              onChange={(event) =>
                                                setJoinCode(event?.target?.value)
                                              }
                                            />
                                            <LoadingButton
                                              loading={actionButtonLoading}
                                              variant="contained"
                                              sx={{ color: 'white' }}
                                              onClick={user ? handleJoinByCode : handleLogin}
                                            >
                                              Join by Code
                                            </LoadingButton>
                                          </>
                                        )}
                                        <Typography
                                          color={(theme) => theme.palette.error.main}
                                          fontWeight="bold"
                                          variant="subtitle2"
                                        >
                                          You must be invited to join this event.
                                        </Typography>
                                      </>
                                    )
                                  ) : registered !== EVENT_REGISTERED.PENDING ? (
                                    <>
                                      {isJoinByCodeActive && (
                                        <>
                                          <TextField
                                            label="Register Code"
                                            value={joinCode}
                                            onChange={(event) => setJoinCode(event?.target?.value)}
                                          />
                                          <LoadingButton
                                            loading={actionButtonLoading}
                                            variant="contained"
                                            sx={{ color: 'white' }}
                                            onClick={user ? handleRegisterByCode : handleLogin}
                                          >
                                            Register by Code
                                          </LoadingButton>
                                        </>
                                      )}
                                      <Typography
                                        color={(theme) => theme.palette.error.main}
                                        fontWeight="bold"
                                        variant="subtitle2"
                                      >
                                        You must be invited to join this event.
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography color="primary.main" fontWeight="bold">
                                      Pending
                                    </Typography>
                                  )
                                ) : (
                                  !parentId && (
                                    <>
                                      <LoadingButton
                                        variant="contained"
                                        sx={{ color: 'white' }}
                                        loading={actionButtonLoading}
                                        onClick={user ? handleRegister : handleLogin}
                                      >
                                        {!isPaid && 'Register'}
                                        {isPaid && `Register $${price}`}
                                      </LoadingButton>
                                      {isJoinByCodeActive && (
                                        <>
                                          <Divider>OR</Divider>
                                          <TextField
                                            label="Register Code"
                                            value={joinCode}
                                            onChange={(event) => setJoinCode(event?.target?.value)}
                                          />
                                          <LoadingButton
                                            loading={actionButtonLoading}
                                            variant="contained"
                                            sx={{ color: 'white' }}
                                            onClick={user ? handleRegisterByCode : handleLogin}
                                          >
                                            Register by Code
                                          </LoadingButton>
                                        </>
                                      )}
                                    </>
                                  )
                                ))}
                              {registered === EVENT_REGISTERED.ACTIVE &&
                                (externalLink ? (
                                  <Button
                                    target="_blank"
                                    href={externalLink ?? ''}
                                    variant="contained"
                                  >
                                    Click to go to event
                                  </Button>
                                ) : (
                                  <Typography color="primary.main" fontWeight="bold">
                                    Upcoming
                                  </Typography>
                                ))}
                            </>
                          )}
                        </>
                      )}

                      {parentId && (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            href={`${paths.programs}/${parentId}`}
                            target="_blank"
                          >
                            Go to program
                          </Button>
                          <Typography
                            color={(theme) => theme.palette.primary.main}
                            fontWeight="bold"
                            variant="subtitle2"
                          >
                            This event is sub-content of a program.
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12}>
                    <div className="eventPostAbout ql-editor">{contentDescription}</div>
                  </Grid>
                </Grid>
                {startDate && (
                  <>
                    <Grid container direction={'row'}>
                      <DateRangeIcon sx={{ mr: 1 }} />
                      <Typography>{startDate}</Typography>
                    </Grid>
                    <ContentRemainingTime
                      endDate={endDate}
                      startDate={details?.startDate ? new Date(details.startDate) : null}
                      progress={eventProgress}
                    />
                  </>
                )}
              </Stack>
            </CardContent>
            <CardActions>
              <Container>
                <Stack spacing={1} direction="row" alignItems="center">
                  {likes.length > 0 && (
                    <>
                      <Tooltip title={`${likedCount} like`}>
                        <AvatarGroup max={3}>
                          {likes.map(
                            (
                              {
                                user: {
                                  detail: { profilePhoto },
                                },
                              },
                              index
                            ) => (
                              <Avatar
                                key={index}
                                src={imageLoader({ src: profilePhoto ? profilePhoto : '' })}
                              />
                            )
                          )}
                        </AvatarGroup>
                      </Tooltip>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{ height: '2.5rem' }}
                      />
                    </>
                  )}

                  <Tooltip title={`Like`}>
                    <Button onClick={user ? handleLikeContent : handleLogin}>
                      <FavoriteIcon
                        sx={{ color: isLike ? '#F86B6A' : '#5c5f64' }}
                        fontSize="large"
                      />
                      <Typography className="eventPostButtonTitle">Like</Typography>
                    </Button>
                  </Tooltip>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{ height: '2.5rem' }}
                  />
                  <Tooltip title={`Comment`}>
                    <Button onClick={toggleComments}>
                      <CommentIcon sx={{ color: '#5c5f64' }} fontSize="large" />
                      <Typography className="eventPostButtonTitle">
                        Comments ({commentCount})
                      </Typography>
                    </Button>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{ height: '2.5rem' }}
                  />
                  {Boolean(affiliateCommissionPercentage) && !owner && user?.isAffiliate ? (
                    <Button
                      onClick={handleShareAffiliateLink}
                      color="success"
                      variant="contained"
                      sx={{ color: 'white' }}
                      startIcon={<KeyboardDoubleArrowUp />}
                    >
                      Promote & Earn $
                      {((content?.price ?? 0) * affiliateCommissionPercentage) / 100}
                    </Button>
                  ) : (
                    <Tooltip title={`Share`}>
                      <Button onClick={handleShare}>
                        <ShareIcon sx={{ color: '#5c5f64' }} fontSize="large" />
                        <Typography className="eventPostButtonTitle">Share</Typography>
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
              </Container>
            </CardActions>
          </Card>
        </Box>
      </Box>
    </>
  );
}
